import { Form, Input, InputNumber, Radio } from 'antd';
import type { TyreStatus } from '@chukua/common';
import type { ReactNode } from 'react';
import type { TyreMoveKind } from '../policies/tyre-move.policy';
import { RotateTargetPicker, type RotationContext } from './RotateTargetPicker';
import { TyreSearchSelect } from './TyreSearchSelect';

/** Module-level so the picker's fetch stays stable between renders. */
const FITTABLE_STATUSES: TyreStatus[] = ['new', 'in_stock'];
const reasonRule = [{ required: true, message: 'Give a reason' }];

const WEAR_FIELD = (
  <Form.Item
    name="treadDepthMm"
    label="Tread depth (mm)"
    rules={[{ required: true, type: 'number', min: 0, max: 40, message: 'Enter a depth between 0 and 40 mm' }]}
  >
    <InputNumber className="w-full" min={0} max={40} step={0.1} precision={1} autoFocus />
  </Form.Item>
);

const DISPOSITION_FIELD = (
  <Form.Item name="disposition" label="Where does it go?" rules={[{ required: true, message: 'Say whether it can be used again' }]}>
    <Radio.Group
      options={[
        { value: 'reusable', label: 'Back to stock — can be used again' },
        { value: 'worn_out', label: 'Worn out — replaced' },
      ]}
      className="flex flex-col gap-1"
    />
  </Form.Item>
);

const REASON_FIELD = (
  <Form.Item name="reason" label="Reason" rules={reasonRule}>
    <Input autoFocus />
  </Form.Item>
);

/** The fields each move asks for, besides the odometer and notes every move takes. */
export function tyreMoveFields(
  kind: TyreMoveKind,
  args: {
    subjectTyreId: string;
    positionOptions?: { value: string; label: string }[];
    rotation?: RotationContext | null;
  },
): ReactNode {
  switch (kind) {
    case 'wear':
      return WEAR_FIELD;
    case 'remove':
      return (
        <>
          {REASON_FIELD}
          {DISPOSITION_FIELD}
        </>
      );
    case 'scrap':
      return REASON_FIELD;
    case 'fit':
      return (
        <Form.Item
          name="tyreId"
          label="Tyre"
          rules={[{ required: true, message: 'Pick the tyre to fit' }]}
          extra="Only new tyres and tyres in stock can be fitted."
        >
          <TyreSearchSelect statuses={FITTABLE_STATUSES} />
        </Form.Item>
      );
    case 'replace':
      return (
        <>
          <Form.Item name="tyreId" label="Replacement tyre" rules={[{ required: true, message: 'Pick the replacement' }]}>
            <TyreSearchSelect statuses={FITTABLE_STATUSES} excludeId={args.subjectTyreId} />
          </Form.Item>
          {REASON_FIELD}
          {DISPOSITION_FIELD}
        </>
      );
    case 'rotate':
      return (
        <Form.Item name="toPositionCode" label="Move to" rules={[{ required: true, message: 'Pick the position' }]}>
          <RotateTargetPicker options={args.positionOptions ?? []} rotation={args.rotation ?? null} />
        </Form.Item>
      );
    default:
      return null;
  }
}

const TOUCHES_VEHICLE: TyreMoveKind[] = ['fit', 'rotate', 'wear', 'mark-worn', 'repair', 'remove', 'scrap', 'replace'];

/** An odometer figure is only meaningful for a move that happens on a vehicle; blank takes the truck's own reading. */
export function OdometerField({ kind }: { kind: TyreMoveKind }) {
  if (!TOUCHES_VEHICLE.includes(kind)) return null;
  return (
    <Form.Item name="odometerKm" label="Odometer (km)" extra="Leave blank to use the truck's current reading. A trailer has no odometer of its own.">
      <InputNumber className="w-full" min={0} precision={0} />
    </Form.Item>
  );
}
